import React, { useEffect, useState, useRef } from "react";
import * as Yup from "yup";
import { Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { updateFetchDetails } from "model/Partners";
import { formatDataBeforeSend } from "helpers/GeneralHelper";
import dayjs from "dayjs";
import InputMask from "react-input-mask";
import { useUserContext } from "libs/context/UserContext";
import { Offcanvas } from "bootstrap";
import CandidateTrackApplication from "pages/CandidateTrackApplication/candidate-track-application";
import { candidateUpdateDocumentsData } from "model/CandidateDetailsModel";
import NotInterestedCommonButton from "pages/NotInterestedCommonButton/not-interested-common-button";

const CandidateDocumentsSection = () => {
    const { candidateDetailsData, fetchCandidateDetailsDataForUpdate, loadingDetailsForUpdate } = useUserContext();
    const driverDetails = candidateDetailsData?.DriverDetails || {};

    const [documentEdit, setDocumentEdit] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const InterestedOrNot = localStorage?.getItem("InterestedOrNot")
    const [InterestedValue, setInterested] = useState(false);

    const inputFields = {
        SSN: "",
        DLNumber: "",
        DLClass: "",
        DLIssuingState: "",
        DLExpiryDate: "",
        MECExpiryDate: "",
        Endorsements: "",
        SSNImageFile: null,
        DLImageFile: null,
        MECImageFile: null,
    };

    const formatOptions = [];
    const [fields, setFields] = useState(inputFields);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const offcanvasRef = useRef(null);
    const offcanvasInstanceRef = useRef(null);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);


    // useEffect(() => {
    //     if (offcanvasRef.current) {
    //         offcanvasInstanceRef.current = new Offcanvas(offcanvasRef.current);
    //     }
    // }, []);
    useEffect(() => {
        if (offcanvasRef.current) {
            offcanvasInstanceRef.current = new Offcanvas(offcanvasRef.current, {
                backdrop: true,   // keeps backdrop
                scroll: true,     // allow body scroll
                keyboard: true    // close on ESC
            });
        }
    }, []);


    const openOffcanvas = () => {
        offcanvasInstanceRef.current?.show();
    };

    const isBlank = (value) => !value || (typeof value === "string" && !value.trim());

    const minExpiryDateTest = (message) =>
        Yup.string()
            .nullable()
            .test("valid-date", "Invalid date format (MM/DD/YYYY)", (value) => {
                if (isBlank(value)) return true;
                return dayjs(value, "MM/DD/YYYY", true).isValid();
            })
            .test("min-date", message, (value) => {
                if (isBlank(value)) return true;
                const inputDate = dayjs(value, "MM/DD/YYYY", true);
                return (
                    inputDate.isValid() &&
                    inputDate.isAfter(dayjs().add(3, "month"), "day")
                );
            });

    const validationSchema = Yup.object().shape({
        SSN: Yup.string().nullable(),
        DLNumber: Yup.string().nullable(),
        DLClass: Yup.string().nullable(),
        DLIssuingState: Yup.string().nullable(),
        DLExpiryDate: minExpiryDateTest(
            "Expiry date must be at least 3 months from today"
        ),
        MECExpiryDate: minExpiryDateTest(
            "MEC expiry date must be at least 3 months from today"
        ),
        Endorsements: Yup.string().nullable(),
    });

    const validateCrossFields = (formFields) => {
        const formErrors = {};

        const hasMecExpiry = !isBlank(formFields.MECExpiryDate);
        const hasMecFileInput = Boolean(formFields.MECImageFile);
        const hasExistingMecFile = Boolean(driverDetails.MECImageLink);
        const hasMecFile = hasMecFileInput || hasExistingMecFile;

        if (!hasMecExpiry && !hasMecFile) {
            return formErrors;
        }

        if (hasMecExpiry && !hasMecFile) {
            formErrors.MECImageFile = "MEC file is required";
        }
        if (!hasMecExpiry && hasMecFile) {
            formErrors.MECExpiryDate = "MEC expiry date is required";
        }

        return formErrors;
    };

    const runValidation = async (formFields) => {
        const formErrors = {};

        try {
            await validationSchema.validate(formFields, { abortEarly: false });
        } catch (err) {
            if (err.name === "ValidationError") {
                err.inner.forEach((e) => {
                    if (e.path) {
                        formErrors[e.path] = e.message;
                    }
                });
            }
        }

        return { ...formErrors, ...validateCrossFields(formFields) };
    };

    const validateField = async (name, value, formFields = fields) => {
        try {
            await Yup.reach(validationSchema, name).validate(value);
            const crossErrors = validateCrossFields(formFields);
            setErrors((prev) => ({
                ...prev,
                [name]: crossErrors[name] || "",
                ...(name === "MECExpiryDate" || name === "MECImageFile"
                    ? {
                        MECImageFile: crossErrors.MECImageFile || "",
                        MECExpiryDate: crossErrors.MECExpiryDate || prev.MECExpiryDate || "",
                    }
                    : {}),
            }));
        } catch (err) {
            setErrors((prev) => ({ ...prev, [name]: err.message }));
        }
    };

    const handleChange = async (e) => {
        const { name, value } = e.target;
        const updatedFields = { ...fields, [name]: value };
        setFields(updatedFields);
        await validateField(name, value, updatedFields);
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched({ ...touched, [name]: true });
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        const updatedFields = { ...fields, [name]: files[0] };
        setFields(updatedFields);
        const crossErrors = validateCrossFields(updatedFields);
        setErrors((prev) => ({
            ...prev,
            ...(crossErrors.MECImageFile !== undefined
                ? { MECImageFile: crossErrors.MECImageFile }
                : { MECImageFile: "" }),
            ...(crossErrors.MECExpiryDate !== undefined
                ? { MECExpiryDate: crossErrors.MECExpiryDate }
                : {}),
        }));
    };

    // const handleSubmit = async (e) => {
    //     e.preventDefault();
    //     setSubmitLoading(true);

    //     try {
    //         await validationSchema.validate(fields, { abortEarly: false });
    //         setErrors({});
    //         const formData = formatDataBeforeSend(fields, inputFields, formatOptions);
    //         let response = await candidateUpdateDocumentsData(formData);

    //         if (response?.IsSuccess) {
    //             toast.success(response?.Message);
    //             fetchCandidateDetailsDataForUpdate()
    //             setDocumentEdit(false);
    //         } else {
    //             toast.error(response?.Message);
    //         }
    //     } catch (err) {
    //         toast.error(err);

    //     } finally {
    //         setSubmitLoading(false);
    //     }
    // };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);

        try {
            const formErrors = await runValidation(fields);
            if (Object.keys(formErrors).length > 0) {
                setErrors(formErrors);
                return;
            }

            setErrors({});

            const formData = formatDataBeforeSend(fields, inputFields, formatOptions);
            let response = await candidateUpdateDocumentsData(formData);

            if (response?.IsSuccess) {
                toast.success(response?.Message);
                fetchCandidateDetailsDataForUpdate();
                // setDocumentEdit(false);
            } else {
                toast.error(response?.Message);
            }
        } catch (err) {
            if (err.name === "ValidationError") {
                // Collect all Yup validation errors
                const formErrors = {};
                err.inner.forEach((e) => {
                    if (e.path) {
                        formErrors[e.path] = e.message;
                    }
                });
                setErrors(formErrors);
            } else {
                // Non-validation errors
                toast.error(err.message || "Something went wrong");
            }
        } finally {
            setSubmitLoading(false);
        }
    };


    const cancelForm = () => setDocumentEdit(true);

    const editDocumentDetails = () => {
        setFields({
            SSN: driverDetails.SSN || "",
            DLNumber: driverDetails.DLNumber || "",
            DLClass: driverDetails.DLClass || "",
            DLIssuingState: driverDetails.DLIssuingState || "",
            DLExpiryDate: driverDetails.DLExpiryDateAppFormat || "",
            MECExpiryDate: driverDetails.MECExpiryDateAppFormat || "",
            Endorsements: driverDetails.Endorsements || "",
            SSNImageFile: driverDetails.SSNImageFile || null,
            DLImageFile: driverDetails.DLImageFile || null,
            MECImageFile: driverDetails.MECImageFile || null,
        });
        setDocumentEdit(true);
    };

    useEffect(() => {
        editDocumentDetails()
    }, [driverDetails]);


    useEffect(() => {
        setInterested(false)
        if (InterestedOrNot == 'Not-Interested') {
            setInterested(false)
        }
        else if (InterestedOrNot == 'Interested') {
            setInterested(true)
        }
    }, [InterestedOrNot]);

    return (
        <>
            {/* <div className="app-status text-end"> */}
            {/* <button className="btn btn-primary-2 w-100-percent"
                    type="button"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#application_status"
                    aria-controls="application_status"
                    onClick={openOffcanvas}
                >
                    Track Application
                </button> */}
            {/* {(candidateDetailsData?.Interested) && */}
                <div className="active-mob-device d-xl-none">
                    <button className="btn btn-primary-2 btn-callback-mob"
                        type="button"
                        data-bs-toggle="offcanvas"
                        data-bs-target="#application_status"
                        aria-controls="application_status"
                        onClick={openOffcanvas}
                    >
                        Track Application
                    </button>

                </div>
            {/* // } */}
            {/* </div> */}
            {(loadingDetailsForUpdate) ? (
                <div className="text-center py-5">
                    <Spinner color="primary" /> Loading...
                </div>
            ) :
                (
                    <div
                        className="tab-pane fade show active"
                        id="pills-Documents"
                        role="tabpanel"
                        aria-labelledby="pills-Documents-tab"
                    >
                        {/* View mode */}
                        {/* View mode */}
                        {!documentEdit && (
                            <>
                                <form className="row mt-4 g-3 container-padding-fix">
                                    <div className="col-12">
                                        <div className="row g-3">
                                            {/* SSN */}
                                            <div className="col-md-6 col-12">
                                                <div className="form-wrapper d-flex justify-content-between align-items-center">
                                                    <div className="d-flex flex-column">
                                                        <label className="form-label">SSN</label>
                                                        <p className="fw-bold text-dark">
                                                            {driverDetails.SSN || "NA"}
                                                        </p>
                                                    </div>
                                                    {driverDetails.SSNImageLink && (
                                                        <div className="icon-button">
                                                            <a
                                                                href={driverDetails.SSNImageLink}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="btn btn-link p-0 text-white"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                                    fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                                                                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                                                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                                                                </svg>
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Driving License */}
                                            <div className="col-md-6 col-12">
                                                <div className="form-wrapper d-flex justify-content-between align-items-center">
                                                    <div className="d-flex flex-column">
                                                        <label className="form-label">Driving License</label>
                                                        <p className="fw-bold text-dark">
                                                            {driverDetails.DLNumber || "NA"}
                                                        </p>
                                                    </div>
                                                    {driverDetails.DLImageLink && (
                                                        <div className="icon-button">
                                                            <a
                                                                href={driverDetails.DLImageLink}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="btn btn-link p-0 text-white"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                                    fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                                                                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                                                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                                                                </svg>
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Class / Issuing State / Expiry Date */}
                                    <div className="col-12">
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <div className="form-wrapper d-flex flex-column">
                                                    <label className="form-label">Class</label>
                                                    <p className="fw-bold text-dark">
                                                        {driverDetails.DLClass || "NA"}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-wrapper d-flex flex-column">
                                                    <label className="form-label">Issuing State</label>
                                                    <p className="fw-bold text-dark">
                                                        {driverDetails.DLIssuingState || "NA"}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-wrapper d-flex flex-column">
                                                    <label className="form-label">Expiry Date</label>
                                                    <p className="fw-bold text-dark">
                                                        {driverDetails.DLExpiryDateAppFormat || "NA"}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>

                                {/* Edit button */}
                                <div className="row mt-3 container-padding-fix">
                                    <div className="col-12">
                                        <div className="d-flex justify-content-end align-items-start">
                                            <button className="btn btn-primary-2" onClick={editDocumentDetails}>
                                                Edit
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}


                        {/* Edit mode */}
                        {documentEdit && (
                            <>
                                <form
                                    className="row mt-4 container-padding-fix"
                                    onSubmit={handleSubmit}
                                >
                                    {/* --- SSN --- */}
                                    <div className="col-md-12 col-12">
                                        <label className="form-label">SSN</label>
                                        <div className="row g-3">
                                            <div className="col-md-6 col-12">
                                                <div className="form-floating">
                                                    {/* <InputMask
                                                mask="999-99-9999"
                                                value={fields.ssn || driverDetails?.SSN || ""}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            >
                                                {(inputProps) => (
                                                    <input
                                                        {...inputProps}
                                                        type="text"
                                                        id="ssn"
                                                        name="ssn"
                                                        placeholder="Enter SSN"
                                                        className="form-control"
                                                    />
                                                )}
                                                    
                                            </InputMask> */}
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="SSN"
                                                        value={fields.SSN || ""}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Enter SSN"
                                                    />
                                                    <label>SSN</label>
                                                </div>
                                                {(touched.SSN || errors.SSN) && (
                                                    <div className="text-danger small">{errors.SSN}</div>
                                                )}
                                            </div>

                                            <div className="col-md-6 col-12">
                                                <div className="row align-items-center">
                                                    <div className={`${driverDetails.SSNImageLink ? 'col-11' : 'col-12'}`}>
                                                        <div className="upload-card col-11">
                                                            <div className="file-upload">
                                                                <div className="file-select">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-secondary rounded-1"
                                                                    >
                                                                        Choose File
                                                                    </button>
                                                                    <div className="file-select-name">
                                                                        {fields.SSNImageFile
                                                                            ? fields.SSNImageFile.name
                                                                            : driverDetails?.SSNImageFile?.name ||
                                                                            "No File Chosen"}
                                                                    </div>
                                                                    <input
                                                                        type="file"
                                                                        name="SSNImageFile"
                                                                        className="profileimg"
                                                                        onChange={handleFileChange}
                                                                        accept="image/png, image/gif, image/jpeg, application/pdf, .doc, .docx"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {driverDetails.SSNImageLink && (
                                                        <div className="icon-button col-1 icon-button-pos-fix">
                                                            <a
                                                                href={driverDetails.SSNImageLink}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="btn btn-link p-0 text-white"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                                    fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                                                                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                                                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                                                                </svg>
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <hr className="my-4" />
                                    </div>

                                    {/* --- Driver's License --- */}
                                    <div className="col-md-12 col-12">
                                        <label className="form-label">Driver's License</label>
                                        <div className="row g-3">
                                            <div className="col-md-6 col-12">
                                                <div className="form-floating">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="DLNumber"
                                                        value={fields.DLNumber}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="License Number"
                                                    />
                                                    <label>License Number</label>
                                                </div>
                                                {(touched.DLNumber || errors.DLNumber) && (
                                                    <div className="text-danger small">{errors.DLNumber}</div>
                                                )}
                                            </div>

                                            <div className="col-md-6 col-12">
                                                <div className="row align-items-center">
                                                    <div className={`${driverDetails.DLImageLink ? 'col-11' : 'col-12'}`}>
                                                        <div className="upload-card">
                                                            <div className="file-upload">
                                                                <div className="file-select">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-secondary rounded-1"
                                                                    >
                                                                        Choose File
                                                                    </button>
                                                                    <div className="file-select-name">
                                                                        {fields.DLImageFile
                                                                            ? fields.DLImageFile.name
                                                                            : driverDetails?.DLImageFile?.name ||
                                                                            "No File Chosen"}
                                                                    </div>
                                                                    <input
                                                                        type="file"
                                                                        name="DLImageFile"
                                                                        className="profileimg"
                                                                        onChange={handleFileChange}
                                                                        accept="image/png, image/gif, image/jpeg, application/pdf, .doc, .docx"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {driverDetails.DLImageLink && (
                                                        <div className="icon-button col-1 icon-button-pos-fix">
                                                            <a
                                                                href={driverDetails.DLImageLink}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="btn btn-link p-0 text-white"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                                    fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                                                                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                                                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                                                                </svg>
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* --- Class / State / Expiry Date --- */}
                                    <div className="col-12 mt-3">
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <div className="form-floating">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="DLClass"
                                                        value={fields.DLClass}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Class"
                                                    />
                                                    <label>Class</label>
                                                </div>
                                                {(touched.DLClass || errors.DLClass) && (
                                                    <div className="text-danger small">{errors.DLClass}</div>
                                                )}
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-floating">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="DLIssuingState"
                                                        value={fields.DLIssuingState}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="State"
                                                    />
                                                    <label>State</label>
                                                </div>
                                                {(touched.DLIssuingState || errors.DLIssuingState) && (
                                                    <div className="text-danger small">{errors.DLIssuingState}</div>
                                                )}
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-floating">
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        name="DLExpiryDate"
                                                        placeholder="MM/DD/YYYY"
                                                        value={
                                                            fields.DLExpiryDate
                                                                ? dayjs(fields.DLExpiryDate, "MM/DD/YYYY").format("YYYY-MM-DD")
                                                                : driverDetails?.DLExpiryDateAppFormat
                                                                    ? dayjs(driverDetails.DLExpiryDateAppFormat, "MM/DD/YYYY").format("YYYY-MM-DD")
                                                                    : ""
                                                        }
                                                        onChange={async (e) => {
                                                            const formatted = e.target.value
                                                                ? dayjs(e.target.value).format("MM/DD/YYYY")
                                                                : "";
                                                            const updatedFields = { ...fields, DLExpiryDate: formatted };
                                                            setFields(updatedFields);
                                                            await validateField("DLExpiryDate", formatted, updatedFields);
                                                        }}
                                                        onBlur={handleBlur}
                                                    />
                                                    <label>Expiry Date</label>
                                                </div>
                                                {(touched.DLExpiryDate || errors.DLExpiryDate) && (
                                                    <div className="text-danger small">{errors.DLExpiryDate}</div>
                                                )}
                                            </div>

                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <hr className="my-4" />
                                    </div>

                                    {/* --- MEC --- */}
                                    <label className="form-label">MEC</label>

                                    <div className="col-md-6 col-12">
                                        <div className="form-floating">
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="MECExpiryDate"
                                                placeholder="MM/DD/YYYY"
                                                value={
                                                    fields.MECExpiryDate
                                                        ? dayjs(fields.MECExpiryDate, "MM/DD/YYYY").format("YYYY-MM-DD")
                                                        : driverDetails?.MECExpiryDateAppFormat
                                                            ? dayjs(driverDetails.MECExpiryDateAppFormat, "MM/DD/YYYY").format("YYYY-MM-DD")
                                                            : ""
                                                }
                                                onChange={async (e) => {
                                                    const formatted = e.target.value
                                                        ? dayjs(e.target.value).format("MM/DD/YYYY")
                                                        : "";
                                                    const updatedFields = { ...fields, MECExpiryDate: formatted };
                                                    setFields(updatedFields);
                                                    await validateField("MECExpiryDate", formatted, updatedFields);
                                                }}
                                                onBlur={handleBlur}
                                            />
                                            <label>MEC Expiry Date</label>
                                        </div>
                                        {(touched.MECExpiryDate || errors.MECExpiryDate) && (
                                            <div className="text-danger small">{errors.MECExpiryDate}</div>
                                        )}
                                    </div>
                                    <div className={`col-md-6 col-12 ${isMobile? 'mt-3' : ''}`}>
                                                <div className="row align-items-center">
                                                    <div className={`${driverDetails.MECImageLink ? 'col-11' : 'col-12'}`}>
                                                        <div className="upload-card">
                                                            <div className="file-upload">
                                                                <div className="file-select">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-secondary rounded-1"
                                                                    >
                                                                        Choose File
                                                                    </button>
                                                                    <div className="file-select-name">
                                                                        {fields.MECImageFile
                                                                            ? fields.MECImageFile.name
                                                                            : driverDetails?.MECImageFile?.name ||
                                                                            "No File Chosen"}
                                                                    </div>
                                                                    <input
                                                                        type="file"
                                                                        name="MECImageFile"
                                                                        className="profileimg"
                                                                        onChange={handleFileChange}
                                                                        accept="image/png, image/gif, image/jpeg, application/pdf, .doc, .docx"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {driverDetails.MECImageLink && (
                                                        <div className="icon-button col-1 icon-button-pos-fix">
                                                            <a
                                                                href={driverDetails.MECImageLink}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="btn btn-link p-0 text-white"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                                    fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                                                                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                                                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                                                                </svg>
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                                {errors.MECImageFile && (
                                                    <div className="text-danger small">{errors.MECImageFile}</div>
                                                )}
                                            </div>


                                    <div className="col-12">
                                        <hr className="my-4" />
                                    </div>

                                    {/* --- Endorsements --- */}
                                    <div className="col-12">
                                        <div className="form-floating">
                                            <textarea
                                                className="form-control"
                                                name="Endorsements"
                                                value={fields.Endorsements}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Leave a comment here"
                                                id="floatingTextarea"
                                            />
                                            <label htmlFor="floatingTextarea">Endorsements</label>
                                        </div>
                                    </div>
                                </form>

                                <div className="d-flex justify-content-end mt-4 container-padding-fix">
                                    <div className="col-auto me-2">
                                        <button
                                            className="btn btn-primary-1"
                                            type="button"
                                            onClick={cancelForm}
                                        >
                                            CANCEL
                                        </button>
                                    </div>
                                    <div className="col-auto">
                                        <button
                                            className="btn btn-primary-2 bg-dark"
                                            type="submit"
                                            disabled={submitLoading}
                                            onClick={handleSubmit}
                                        >
                                            {submitLoading ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" /> Saving...
                                                </>
                                            ) : (
                                                "SAVE"
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        <hr />
                        {/* <div className="row justify-content-center">
                    <div className="col-auto">
                        <button className="btn btn-primary-1">Not Interested Anymore</button>
                    </div>
                </div> */}
                        {/* <NotInterestedCommonButton /> */}

                        <CandidateTrackApplication offcanvasRef={offcanvasRef} offcanvasInstanceRef={offcanvasInstanceRef} />
                    </div>
                )}
        </>
    );
};

export default CandidateDocumentsSection;
